import React from 'react';
import { logger } from './utils/logger';

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Log the error
        logger.error('React Error Boundary caught an error:', {
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack
        });

        this.setState({
            error,
            errorInfo
        });
    }

    handleReload = () => {
        logger.info('User triggered app reload after error');
        window.location.reload();
    };

    handleReset = () => {
        logger.info('User reset error boundary');
        this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div role="alert" style={{
                    padding: '40px',
                    maxWidth: '600px',
                    margin: '50px auto',
                    backgroundColor: 'var(--redo-surface)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
                    <h2 style={{ color: 'var(--redo-danger)', marginBottom: '16px' }}>Something went wrong</h2>
                    <p style={{ color: 'var(--redo-text-muted)', marginBottom: '24px' }}>
                        The application encountered an unexpected error. This has been logged for debugging.
                    </p>

                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        justifyContent: 'center',
                        marginBottom: '24px'
                    }}>
                        <button
                            onClick={this.handleReset}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: 'var(--redo-accent)',
                                color: 'var(--redo-text)',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}
                        >
                            Try Again
                        </button>
                        <button
                            onClick={this.handleReload}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: 'var(--redo-surface-hover)',
                                color: 'var(--redo-text)',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '500'
                            }}
                        >
                            Reload Page
                        </button>
                    </div>

                    {this.state.error && (
                        <details style={{
                            textAlign: 'left',
                            backgroundColor: 'var(--redo-bg-elevated)',
                            padding: '16px',
                            borderRadius: '8px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <summary style={{
                                cursor: 'pointer',
                                fontWeight: '600',
                                marginBottom: '8px'
                            }}>
                                Error Details
                            </summary>
                            <pre style={{
                                fontSize: '12px',
                                color: 'var(--redo-text)',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                                {this.state.error.message}
                                {this.state.error.stack && (
                                    <>
                                        {'\n\nStack Trace:\n'}
                                        {this.state.error.stack}
                                    </>
                                )}
                            </pre>
                        </details>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;